import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import { formatDistance, getUnixTime } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Subject,
  takeUntil,
} from 'rxjs';

import { Employment } from 'server/shared/models/employment';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { CustomerService } from '../../services/customer.service';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-dispo-employment-details-table',
  templateUrl: './dispo-employment-details-table.component.html',
  styleUrls: ['./dispo-employment-details-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispoEmploymentDetailsTableComponent implements OnDestroy {
  private destroyed$ = new Subject<void>();

  i18n = this.translationService.getTranslations('employments');

  employment$ = new BehaviorSubject<Employment | undefined>(undefined);
  @Input() set employment(employment: Employment | undefined) {
    this.employment$.next(employment);
  }
  get employment(): Employment | undefined {
    return this.employment$.getValue();
  }

  transferCounts$ = combineLatest([
    this.employment$.pipe(filter(filterIsNotEmpty), takeUntil(this.destroyed$)),
    this.customerService.readyDispoCustomer$.pipe(takeUntil(this.destroyed$)),
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([employment, customer]) => {
      const isOwnEmployment =
        customer && employment.customer.id === customer._id;
      if (!employment.transfers?.length || !isOwnEmployment) {
        return undefined;
      }
      const pendingAutoTransfers = employment.transfers
        .filter((t) => t.transferAt && !t.transferredAt && t.auto)
        .map((t) => t.transferAt)
        // second filter just for typescript, is already covered in the filter above
        .filter(filterIsNotEmpty);
      const nextAutoTransfer = pendingAutoTransfers.sort(
        (a, b) => getUnixTime(a) - getUnixTime(b),
      )[0];
      return {
        total: employment.transfers.length,
        transferred: employment.transfers.filter(
          (t) => t.transferredAt && !t.declinedAt,
        ).length,
        declined: employment.transfers.filter((t) => t.declinedAt).length,
        auto: pendingAutoTransfers.length,
        nextAutoTransfer,
      };
    }),
  );

  transferredToMeText$ = combineLatest([
    this.employment$.pipe(filter(filterIsNotEmpty), takeUntil(this.destroyed$)),
    this.customerService.readyDispoCustomer$.pipe(takeUntil(this.destroyed$)),
    this.userService.readyDispo$.pipe(takeUntil(this.destroyed$)),
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([employment, loggedInCustomer, loggedInDispo]) => {
      const transfer = employment?.transfers?.find(
        (transfer) => transfer.targetCustomer.id === loggedInCustomer._id,
      );
      if (!transfer || !transfer.transferredAt) {
        return;
      }
      if (transfer.declinedAt) {
        return (
          employment.customer.displayName +
          (this.i18n?.declined || 'abgelehnt') +
          ' ' +
          formatDistance(transfer.declinedAt, new Date(), {
            addSuffix: true,
            locale: loggedInDispo.locale === 'en' ? enUS : de,
          })
        );
      }
      return (
        employment.customer.displayName +
        ': ' +
        (this.i18n?.transferred || 'transferiert') +
        ' ' +
        formatDistance(transfer.transferredAt, new Date(), {
          addSuffix: true,
          locale: loggedInDispo.locale === 'en' ? enUS : de,
        })
      );
    }),
  );
  isDispo$ = this.userService.isDispo$.pipe(takeUntil(this.destroyed$));

  employmentCertificates$ = this.employment$.pipe(
    filter(filterIsNotEmpty),
    takeUntil(this.destroyed$),
    map((employment) => {
      if (!employment.certificates?.length) {
        return;
      }
      return employment.certificates.map((c) => c.displayName).join(', ');
    }),
  );

  constructor(
    private translationService: TranslationService,
    private customerService: CustomerService,
    private userService: UserService,
  ) {}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
